/* eslint-disable @typescript-eslint/no-explicit-any */

import { useRouter } from "next/navigation";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { APPLICATION } from "@/constants";
import { RootState } from "@/store/root-reducer";
import { GetQuoteFilter } from "@/store/slices/quote/API/getQuote";
import { getQuoteData } from "@/store/slices/quote/quote.action";
import { storageAvailable } from "@/utils/common.utils";

const withQuestionQuoteData = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  function WithSessionData(props: P) {
    const dispatch = useDispatch<any>();
    const router = useRouter();
    const quoteResponse = useSelector((state: RootState) => state.quote);
    const stringWithoutSlashes =
      typeof window !== "undefined"
        ? window.location.pathname.replace(/\//g, "")
        : "";
    const [renderFlag, setRenderFlag] = React.useState(false);

    const clearPageReload = useCallback(
      (quoteReferenceId: string | undefined) => {
        if (
          !storageAvailable() ||
          APPLICATION !== "renewal" ||
          !quoteReferenceId
        )
          return;
        if (sessionStorage.getItem("pageReload")) {
          sessionStorage.removeItem("pageReload");
          setRenderFlag(!renderFlag);
        }
      },
      [renderFlag]
    );

    const handleFocus = useCallback(() => {
      setTimeout(() => clearPageReload(quoteResponse?.referenceId), 2000);
    }, [clearPageReload, quoteResponse?.referenceId]);

    useEffect(() => {
      window.addEventListener("focus", handleFocus);
      return () => window.removeEventListener("focus", handleFocus);
    }, [handleFocus]);

    useEffect(() => {
      if (quoteResponse) {
        const { referenceId } = quoteResponse;

        const handleBeforeUnload = (event: { preventDefault: () => void }) => {
          if (storageAvailable()) {
            sessionStorage.setItem("pageReload", "true");
          }

          if (referenceId && storageAvailable()) {
            event.preventDefault();
            sessionStorage.removeItem("organiserTravellerId");
          }
        };

        const isOnIOS =
          navigator.userAgent.match(/iPad/i) ||
          navigator.userAgent.match(/Macintosh/i) ||
          navigator.userAgent.match(/iPhone/i);
        const eventName = isOnIOS ? "pagehide" : "beforeunload";

        window.addEventListener(eventName, handleBeforeUnload);
        return () => {
          return window.removeEventListener(eventName, handleBeforeUnload);
        };
      }

      return undefined;
    }, [stringWithoutSlashes, quoteResponse]);

    function quoteCallCheck() {
      if (
        storageAvailable() &&
        (sessionStorage.getItem("pageReload") ||
          !sessionStorage.getItem("initialLoad"))
      ) {
        return true;
      }

      return false;
    }

    const fetchQuoteDataComponent = async () => {
      const getQuoteFilter: GetQuoteFilter = {
        affiliate: null,
        quoteId: null,
      };

      if (quoteCallCheck() && !quoteResponse?.referenceId) {
        if (stringWithoutSlashes) {
          sessionStorage.setItem("initialLoad", "true");
          sessionStorage.removeItem("pageReload");
          await dispatch(getQuoteData({ getQuoteFilter, router, dispatch }));
        }
      }
      return null; // Ensure the function returns null if no action is needed
    };

    useEffect(() => {
      fetchQuoteDataComponent();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...props} />;
  }

  return WithSessionData;
};

export default withQuestionQuoteData;
